import React, { useState, useEffect, useCallback } from 'react';
import API from 'shared/api';
import {
  FiX,
  FiCheck,
  FiAlertCircle,
  FiChevronRight,
  FiArrowLeft,
  FiTrendingUp,
  FiTrendingDown,
} from 'react-icons/fi';
import { SS, fmtFecha, fmtRelativo, fmtDuracion } from './syncUtils';

const ORIGEN_LABEL = {
  programada: 'Automatica',
  manual: 'Manual',
  instalacion: 'Primera sincronización',
};

function origenLabel(o) {
  return ORIGEN_LABEL[o] || (o ? o.charAt(0).toUpperCase() + o.slice(1) : 'Sincronización');
}

export default function SyncHistorialModal({ sede, onClose }) {
  const sedeCod = sede.sede_codigo;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [ultimoId, setUltimoId] = useState(null);
  const [sel, setSel] = useState(null);
  const [detalle, setDetalle] = useState([]);
  const [detLoading, setDetLoading] = useState(false);

  const cargar = useCallback(
    async (cursor) => {
      setLoading(true);
      try {
        const url = `/sync/historial/${encodeURIComponent(sedeCod)}?limite=20${cursor ? `&ultimo_id=${cursor}` : ''}`;
        const res = await API.get(url);
        if (res.data.status === 'success') {
          setItems((prev) => (cursor ? [...prev, ...res.data.data] : res.data.data));
          setHasMore(res.data.pagination?.has_more || false);
          setUltimoId(res.data.pagination?.ultimo_id || null);
        }
      } catch (e) {
        // silencioso: la UI muestra estado vacio
      } finally {
        setLoading(false);
      }
    },
    [sedeCod]
  );

  useEffect(() => {
    cargar(null);
  }, [cargar]);

  const abrirDetalle = async (item) => {
    setSel(item);
    setDetalle([]);
    setDetLoading(true);
    try {
      const res = await API.get(
        `/inventario/cambios/${encodeURIComponent(sedeCod)}?batch_id=${encodeURIComponent(item.batch_id)}`
      );
      if (res.data.status === 'success') setDetalle(res.data.data.cambios || []);
    } catch (e) {
      setDetalle([]);
    } finally {
      setDetLoading(false);
    }
  };

  return (
    <div style={SS.overlay} onClick={onClose}>
      <div
        className="sync-modal"
        style={{
          background: '#fff',
          borderRadius: '18px',
          width: '100%',
          maxWidth: '560px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '20px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #f3f4f6',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {sel && (
              <button
                onClick={() => setSel(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '2px',
                  display: 'flex',
                }}
              >
                <FiArrowLeft size={18} />
              </button>
            )}
            <div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#111827' }}>
                {sel ? 'Detalle de cambios' : 'Historial de sincronizaciones'}
              </div>
              <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                {sel ? fmtFecha(sel.fin || sel.inicio) : sede.sede_nombre || sedeCod}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#9ca3af',
              padding: '4px',
              display: 'flex',
            }}
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="sync-modal-body" style={{ padding: '16px 24px 24px', overflowY: 'auto' }}>
          {!sel && (
            <>
              {loading && items.length === 0 && (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#9ca3af',
                    fontSize: '13px',
                  }}
                >
                  Cargando historial...
                </div>
              )}
              {!loading && items.length === 0 && (
                <div style={{ textAlign: 'center', padding: '48px 20px', color: '#9ca3af' }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>🕒</div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#6b7280' }}>
                    Aún no hay sincronizaciones
                  </div>
                  <div style={{ fontSize: '12px', marginTop: '4px' }}>
                    Cuando el sincronizador corra, verás aquí el registro.
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {items.map((h) => {
                  const ok = h.estado === 'exito';
                  return (
                    <button
                      key={h.id}
                      onClick={() => abrirDetalle(h)}
                      className="sync-row"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 14px',
                        borderRadius: '12px',
                        border: '1px solid #eef0f4',
                        background: '#fff',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        textAlign: 'left',
                      }}
                    >
                      <div
                        style={{
                          width: '34px',
                          height: '34px',
                          borderRadius: '10px',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: ok ? '#ecfdf5' : '#fef2f2',
                        }}
                      >
                        {ok ? (
                          <FiCheck size={17} color="#059669" />
                        ) : (
                          <FiAlertCircle size={17} color="#dc2626" />
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>
                          {origenLabel(h.origen)}
                        </div>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                          {fmtRelativo(h.fin || h.inicio)} · {fmtDuracion(h.duracion_ms)}
                        </div>
                      </div>
                      {ok ? (
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: '#111827' }}>
                            {h.productos}
                          </div>
                          <div style={{ fontSize: '10px', color: '#9ca3af' }}>productos</div>
                        </div>
                      ) : (
                        <span style={{ ...SS.chip, background: '#fef2f2', color: '#dc2626' }}>
                          Fallo
                        </span>
                      )}
                      <FiChevronRight size={16} color="#d1d5db" style={{ flexShrink: 0 }} />
                    </button>
                  );
                })}
              </div>
              {hasMore && (
                <div style={{ textAlign: 'center', marginTop: '14px' }}>
                  <button onClick={() => cargar(ultimoId)} disabled={loading} style={SS.btnOutline}>
                    {loading ? 'Cargando...' : 'Ver mas'}
                  </button>
                </div>
              )}
            </>
          )}

          {sel && (
            <div className="sync-anim">
              <div
                className="sync-grid"
                style={{
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  marginBottom: '16px',
                }}
              >
                <Stat label="Productos" valor={sel.productos} />
                <Stat label="Cambios de precio" valor={sel.cambios_precio} color="#2563eb" />
                <Stat label="Cambios de stock" valor={sel.cambios_stock} color="#d97706" />
              </div>
              {detLoading && (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '30px',
                    color: '#9ca3af',
                    fontSize: '13px',
                  }}
                >
                  Cargando cambios...
                </div>
              )}
              {!detLoading && detalle.length === 0 && (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '30px',
                    color: '#9ca3af',
                    fontSize: '13px',
                  }}
                >
                  Sin cambios de precio ni stock en esta sincronización.
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {detalle.map((c, i) => (
                  <div
                    key={i}
                    style={{
                      padding: '10px 14px',
                      borderRadius: '10px',
                      border: '1px solid #eef0f4',
                      background: '#fafbfc',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#111827',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {c.nombre || c.codigo}
                    </div>
                    <div
                      style={{ display: 'flex', gap: '16px', marginTop: '4px', flexWrap: 'wrap' }}
                    >
                      {c.precio_anterior != null && (
                        <span
                          style={{
                            fontSize: '11px',
                            color: '#6b7280',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          {c.precio_nuevo >= c.precio_anterior ? (
                            <FiTrendingUp size={12} color="#059669" />
                          ) : (
                            <FiTrendingDown size={12} color="#dc2626" />
                          )}
                          Precio: {c.precio_anterior} →{' '}
                          <strong style={{ color: '#111827' }}>{c.precio_nuevo}</strong>
                        </span>
                      )}
                      {c.stock_anterior != null && (
                        <span
                          style={{
                            fontSize: '11px',
                            color: '#6b7280',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          Stock: {c.stock_anterior} →{' '}
                          <strong style={{ color: '#111827' }}>{c.stock_nuevo}</strong>
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, valor, color }) {
  return (
    <div
      style={{
        background: '#f8f9fb',
        borderRadius: '12px',
        padding: '12px 14px',
        textAlign: 'center',
        border: '1px solid #eef0f4',
      }}
    >
      <div style={{ fontSize: '20px', fontWeight: 700, color: color || '#111827' }}>
        {valor || 0}
      </div>
      <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{label}</div>
    </div>
  );
}
